import { computed, Signal, signal, untracked } from '@angular/core';
import { CustomFieldDto, CustomFieldFormat, DataCurrencyDisplay, DataCurrencyLayout, DataCurrencyNegativeDisplay, DataCurrencySign, DataDateFormat, DataFieldTimeValueKind, DataFieldUsageHints, DataNumberFormat, DataTimeFormat, LiteralType, NumberRoundingMethod, SchemaFieldTargetDto, ValueDataType } from 'src/app/services/api.services';
import { InsightsQuery, InsightsQuerySyntax } from 'src/insightsQuery';
import { DataDisplayOptions, DataFormat, DataSchema } from '../dataModel';
import { DataType } from '../dataModel/DataType';
import { DataQueryExpr } from '../dataQuery';
import { CustomFieldIssue } from './customFields/CustomFieldIssue';
import { CustomFieldSyntaxVisitor } from './customFields/CustomFieldSyntaxVisitor';
import { VisualCustomFieldTarget } from './VisualFieldTarget';

let nextCustomFieldId = 1;

export class VisualCustomField {
  readonly id = nextCustomFieldId++;

  constructor(private readonly _init: VisualCustomField.Init) { }

  readonly context = this._init.context;

  readonly editorItem = signal<any>(null);

  readonly parent = this._init.parent;
  readonly name = signal<string>(this._init?.name ?? this._init?.dto?.name ?? '');
  readonly longTitle = computed(() => this.formatLongTitle());

  readonly sourceText = signal<string>(this._init?.sourceText ?? this._init?.dto?.sourceText ?? '');

  readonly format = signal<CustomFieldFormat>(this._init?.dto?.format ?? CustomFieldFormat.Unknown);
  readonly decimalPrecision = signal<number | null>(this._init?.dto?.decimalPrecision ?? 2);
  readonly numberRounding = signal<NumberRoundingMethod>(this._init?.dto?.numberRounding ?? NumberRoundingMethod.Truncate);
  readonly showThousandSeparator = signal<boolean>(this._init?.dto?.showThousandSeparator ?? true);
  readonly significantDigitsOnly = signal<boolean>(this._init?.dto?.significantDigitsOnly ?? false);

  readonly currency = signal<DataCurrencyDisplay>(this._init?.dto?.currency ?? { ...DataDisplayOptions.defaults.currency });
  
  readonly nullText = signal<string>(this._init?.dto?.nullText ?? '#NULL');
  
  readonly dateFormat = signal<DataDateFormat>(this._init?.dto?.dateFormat ?? DataDateFormat.Short);
  readonly timeFormat = signal<DataTimeFormat>(this._init?.dto?.timeFormat ?? DataTimeFormat.Full);
  
  readonly dataFormat = computed<Readonly<DataFormat> | null>(() => {
    return {
      treatNullAsBlank: false,
    };
  });

  readonly dataDisplay = computed<Readonly<DataDisplayOptions> | null>(() => {
    // HardCoded for IN-7626

    return {
      numberRounding: {
        method:    NumberRoundingMethod.Nearest, // this.numberRounding(),
        precision: 2, // this.decimalPrecision(),
      },
      
      numberFormat: DataNumberFormat.Default,
        //this.format() == CustomFieldFormat.Currency   ? DataNumberFormat.Currency   :
        //this.format() == CustomFieldFormat.Percentage ? DataNumberFormat.Percentage : DataNumberFormat.Default,

      showThousandSeparator: true, //this.showThousandSeparator(),
      significantDigitsOnly: false, //this.significantDigitsOnly(),

      currency: this.currency(),

      nullQueryExpr: DataQueryExpr.createStringLiteralSyntaxTree(this.nullText()),
      numericAggregationNullQueryExpr: DataQueryExpr.createLiteralSyntaxTree(LiteralType.Integer, 0),

      dateFormat: this.dateFormat(),
      timeFormat: this.timeFormat(),
    };
  });

  /** Gets expression information computed from this custom field's source text. */
  readonly expr = computed<VisualCustomField.Expr>(() => {
    let [fieldResolver, functionResolver] = untracked(() => [
      this.context.fieldResolver(),
      this.context.functionResolver()
    ]);

    let syntax: InsightsQuerySyntax = null;
    let parseErrors: InsightsQuery.ParseError[] = [];
    let issues: CustomFieldIssue[] = [];

    let dataType = DataType.Text;
    let isAggregated = false;
    let currencyVariants = new Map<string, InsightsQuerySyntax>();

    try {
      syntax = InsightsQuery.parse(this.sourceText(), parseErrors, false);
      if (syntax) {
        let visitResult = CustomFieldSyntaxVisitor.visit(syntax, {
          field: this.identifierExpr(),
          fieldResolver,
          functionResolver,
          skipCircularDependencyCheck: false
        });

        dataType = visitResult.dataType;
        isAggregated = visitResult.aggregated;
        // TODO: currencyVariants

        issues.push(...visitResult.issues);
      }
    }
    catch (err) {
      parseErrors.push(
        new InsightsQuery.ParseError(
          InsightsQuery.TextLocation.first,
          err instanceof Error ? err.message : '' + err
        ));
    }

    issues.push(...parseErrors.map(x => <CustomFieldIssue>{
      type: CustomFieldIssue.Type.Error,
      location: x.location,
      message: x.message
    }));

    return {
      syntax,

      dataType,
      isAggregated,
      currencyVariants,

      parseErrors,
      issues,

      anyErrors: issues.some(i => i.type == CustomFieldIssue.Type.Error)
    };
  });

  /** Gets the syntax parsed from this custom field's source text. */
  readonly syntax = computed(() => this.expr().syntax);

  /**
   * Gets the data type this custom field's expression evaluates to.
   * Null when the expression is invalid. */
  readonly dataType = computed(() => {
    if (this.expr().dataType.isNumeric)
      return DataType.Number
    else
      return DataType.Text


    //Disabled as of IN-7626

    let format = this.format();
    if (format === CustomFieldFormat.Unknown) {
      return this.expr().dataType;
    }
    else {
      switch (format) {
        case CustomFieldFormat.Currency:
          return DataType.Decimal;
          
        case CustomFieldFormat.Number:
        case CustomFieldFormat.Percentage:
          return DataType.Number;
          
        case CustomFieldFormat.DateOnly:
          return DataType.DateOnly;

        case CustomFieldFormat.DateTime:
          return DataType.DateTime;
          
        case CustomFieldFormat.TimeOnly:
          return DataType.TimeOnly;

        case CustomFieldFormat.Text:
        default:
          return DataType.Text;
      }
    }
  });

  /**
   * Gets whether the custom field's expression returns aggregated data.
   * Null when the expression is invalid. */
  readonly isAggregated = computed(() => this.expr().isAggregated);

  /**
   * Gets currency variant map of this custom field's expression.
   * This is an empty map if there is no currency variants applicable,
   * or the expression is invalid. */
  readonly currencyVariants = computed(() => this.expr().currencyVariants);


  /**
   * Gets a collection of issues that resulted when parsing this custom field's source text.
   * Returns an empty array when there has been no issues.
   * Note - this collection includes both errors and non-error issues. */
  readonly issues = computed(() => this.expr().issues ?? []);

  /**
   * Whether any errors occurred when parsing this custom field's source text. */
  readonly hasErrors = computed(() => !!this.expr().anyErrors);


  /**
   * Gets an expression string that can be used to identify this custom field
   * in InsightsQuery or DataQueryExpr code. */
  readonly identifierExpr = computed(() => this.getIdentifierExpr());

  /**
   * Gets a collection of children of this custom field.
   * The children are populated depending on the custom field's data type, e.g.
   * a data or date&time value will have children for its elements such as year, month, etc.
   * Returns an empty array when there is no children, or when the expression is invalid. */
  readonly children = computed(() => [...VisualCustomField.createChildren(this)]);

  findChild(path: ReadonlyArray<string>, recursive: boolean) {
    return this._findChild(path, 0, recursive);
  }

  private _findChild(path: ReadonlyArray<string>, index: number, recursive: boolean) {
    let name  = path[index].toLowerCase();
    let child = this.children().find(x => x.name().toLowerCase() == name);
    if (child) {
      if (index == (path.length - 1))
        return child;

      if (!recursive)
        return null;

      return child._findChild(path, index + 1, recursive);
    }

    return null;
  }


  readonly target = computed(() => new VisualCustomFieldTarget(this, null));

  readonly editorIcon = computed(() => VisualCustomField.EDITOR_ICON);

  readonly usageHints = signal<DataFieldUsageHints>(null);


  /**
   * Constructs long title of this schema item, e.g.
   * 'Group - Item'
   * 'Group - Parent Item - Item'
   */
  formatLongTitle(separator = ' - ') {
    return [VisualCustomField.GROUP_NAME, this.name()].join(separator);
  }

  getIdentifierName(): string {
    return this.formatLongTitle('.');
  }

  getIdentifierExpr(): string {
    return `[${this.getIdentifierName()}]`;
  }

  getIdentifierExprSyntax() {
    return InsightsQuery.parse(this.getIdentifierExpr());
  }

  createSchemaFieldTargetDto(): SchemaFieldTargetDto {
    return {
      schema: this.context.schema.getSchemaTargetDto(),
      group: VisualCustomField.GROUP_NAME,
      field: this.name(),
      childField: null,
      aggregation: null,
      displayName: null,
    };
  }

  createCustomFieldDto(): CustomFieldDto {
    return {
      name: this.name(),
      sourceText: this.sourceText(),

      dataType: this.dataType()?.value ?? ValueDataType.Unknown,
      format: this.format(),
      
      decimalPrecision: this.decimalPrecision(),
      numberRounding: this.numberRounding(),
      showThousandSeparator: this.showThousandSeparator(),
      significantDigitsOnly: this.significantDigitsOnly(),

      currency: this.currency(),
      
      nullText: this.nullText(),
      
      dateFormat: this.dateFormat(),
      timeFormat: this.timeFormat(),

      isAggregated: !!this.expr()?.isAggregated,
      hasErrors: !!this.expr()?.anyErrors,

      usageHints: this.usageHints()
    };
  }

  updateFromDto(dto: CustomFieldDto) {
    this.name.set(dto.name);
    this.sourceText.set(dto.sourceText);

    this.format.set(dto.format);

    this.decimalPrecision.set(dto.decimalPrecision);
    this.numberRounding.set(dto.numberRounding);
    this.showThousandSeparator.set(dto.showThousandSeparator);
    this.significantDigitsOnly.set(dto.significantDigitsOnly);

    this.currency.set(dto.currency);

    this.nullText.set(dto.nullText);

    this.dateFormat.set(dto.dateFormat);
    this.timeFormat.set(dto.timeFormat);

    this.usageHints.set(dto.usageHints);
  }
}

export namespace VisualCustomField {
  export const GROUP_NAME = 'Calculated Fields';
  export const EDITOR_ICON = 'far fa-calculator';

  export interface Context {
    readonly schema: DataSchema;
    readonly fieldResolver: Signal<CustomFieldSyntaxVisitor.FieldResolver>;
    readonly functionResolver: Signal<CustomFieldSyntaxVisitor.FunctionResolver>;
  }

  export interface Init {
    readonly context: Context;

    readonly parent?: VisualCustomField;
    readonly name?: string;
    readonly sourceText?: string;

    readonly dto?: CustomFieldDto;
  }

  export interface Expr {
    readonly syntax: InsightsQuerySyntax | null;

    readonly dataType: DataType | null;
    readonly isAggregated: boolean;
    readonly currencyVariants: ReadonlyMap<string, InsightsQuerySyntax>;

    readonly parseErrors: ReadonlyArray<InsightsQuery.ParseError>;
    readonly issues: ReadonlyArray<CustomFieldIssue>;

    readonly anyErrors: boolean;
  }

  // TODO: This should be driven by the backend? VisualCustomField.createChildren
  export function *createChildren(parent: VisualCustomField) {
    let dataType = parent.dataType();

    let hasDate = dataType.isDateTime || dataType.isDateOnly;
    let hasTime = dataType.isDateTime || dataType.isTimeOnly;

    if (!hasDate && !hasTime)
      return;

    let identifierExpr = parent.identifierExpr();

    if (hasDate) {
      yield new VisualCustomField({
        context: parent.context,

        parent,
        sourceText: `GET_YEAR(${identifierExpr})`,
        name: 'Year',
        dto: {
          format: CustomFieldFormat.Number,
          numberRounding: NumberRoundingMethod.Floor,
          decimalPrecision: 0,
          showThousandSeparator: false,
          usageHints: { timeValueKind: DataFieldTimeValueKind.Year },
        },
      });

      yield new VisualCustomField({
        context: parent.context,

        parent,
        sourceText: `GET_YEAR_AND_QUARTER(${identifierExpr})`,
        name: 'Year & Quarter',
        dto: {
          format: CustomFieldFormat.Text,
          usageHints: { timeValueKind: DataFieldTimeValueKind.YearAndQuarter },
        }
      });

      yield new VisualCustomField({
        context: parent.context,

        parent,
        sourceText: `GET_QUARTER_NAME(${identifierExpr})`,
        name: 'Quarter',
        dto: {
          format: CustomFieldFormat.Text,
          usageHints: { timeValueKind: DataFieldTimeValueKind.Quarter },
        }
      });

      yield new VisualCustomField({
        context: parent.context,

        parent,
        sourceText: `GET_YEAR_AND_MONTH(${identifierExpr})`,
        name: 'Year & Month',
        dto: {
          format: CustomFieldFormat.Text,
          usageHints: { timeValueKind: DataFieldTimeValueKind.YearAndMonth },
        }
      });

      yield new VisualCustomField({
        context: parent.context,

        parent,
        sourceText: `GET_MONTH(${identifierExpr})`,
        name: 'Month',
        dto: {
          format: CustomFieldFormat.Number,
          numberRounding: NumberRoundingMethod.Floor,
          decimalPrecision: 0,
          showThousandSeparator: false,
          usageHints: { timeValueKind: DataFieldTimeValueKind.Month },
        },
      });

      yield new VisualCustomField({
        context: parent.context,

        parent,
        sourceText: `GET_MONTH_NAME(${identifierExpr})`,
        name: 'Month Name',
        dto: {
          format: CustomFieldFormat.Text,
          usageHints: { timeValueKind: DataFieldTimeValueKind.Month },
        }
      });

      yield new VisualCustomField({
        context: parent.context,

        parent,
        sourceText: `GET_WEEKDAY_NAME(${identifierExpr})`,
        name: 'Day of Week',
        dto: {
          format: CustomFieldFormat.Text,
          usageHints: { timeValueKind: DataFieldTimeValueKind.WeekDay },
        }
      });

      yield new VisualCustomField({
        context: parent.context,

        parent,
        sourceText: `GET_WEEKDAY(${identifierExpr})`,
        name: 'Day',
        dto: {
          format: CustomFieldFormat.Number,
          numberRounding: NumberRoundingMethod.Floor,
          decimalPrecision: 0,
          showThousandSeparator: false,
          usageHints: { timeValueKind: DataFieldTimeValueKind.Day },
        },
      });
    }

    if (hasDate && hasTime) {
      yield new VisualCustomField({
        context: parent.context,

        parent,
        sourceText: `GET_DATE(${identifierExpr})`,
        name: 'Date',
        dto: {
          format: CustomFieldFormat.DateOnly,
          usageHints: { timeValueKind: DataFieldTimeValueKind.DateTime },
        }
      });

      yield new VisualCustomField({
        context: parent.context,
        
        parent,
        sourceText: `GET_TIME(${identifierExpr})`,
        name: 'Time',
        dto: {
          format: CustomFieldFormat.TimeOnly,
          usageHints: { timeValueKind: DataFieldTimeValueKind.DateTime },
        }
      });
    }
  }

  export function dataTypeFrom(format: CustomFieldFormat) {
    switch (format) {
      case CustomFieldFormat.Currency:
        return DataType.Decimal;
        
      case CustomFieldFormat.DateOnly:
        return DataType.DateOnly;
        
      case CustomFieldFormat.DateTime:
        return DataType.DateTime;
        
      case CustomFieldFormat.Number:
      case CustomFieldFormat.Percentage:
        return DataType.Number;
        
      case CustomFieldFormat.Text:
        return DataType.Text;
        
      case CustomFieldFormat.TimeOnly:
        return DataType.TimeOnly;

      default: return DataType.Unknown;
    }
  }
}
